import { createContext } from "preact";
import { useCallback, useState, useEffect } from "preact/hooks";
import { initRecharge, loginShopifyAppProxy, listCharges } from "@rechargeapps/storefront-client";

// API Cache
export const ApiCacheContext = createContext({})

export function ApiCacheProvider({ children }) {
    const [cache, setCache] = useState({})
    
    const getCached = useCallback(key => cache[key], [cache])
    
    const setCached = useCallback((key, data) => {
        setCache(prev => ({ ...prev, [key]: data }))
    }, [])
    
    const clearCache = useCallback(key => {
        if (key) {
            setCache(prev => {
                const newCache = { ...prev }
                delete newCache[key]
                return newCache
            })
        } else {
            setCache({})
        }
    }, [])
    
    return (
        <ApiCacheContext.Provider value={{ getCached, setCached, clearCache }}>
            {children}
        </ApiCacheContext.Provider>
    )
}

// Data
export const DataContext = createContext({})

// Auth
export const AuthContext = createContext({});

export function AuthProvider({ children }) {
    // Auth relies on Shopify's protected /account/ route + Customer Account API for mutations
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [customer, setCustomer] = useState(null);
    const [error, setError] = useState(null);
    const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
    const [subscriptionLoading, setSubscriptionLoading] = useState(true);

    const checkSubscription = useCallback(async (customerId) => {
        try {
            setSubscriptionLoading(true);
            
            // Whitelisted customers who bypass subscription check
            const whitelistedCustomers = [
                '8463093825732',
                '8384442335428',
                'gid://shopify/Customer/8463093825732',
                'gid://shopify/Customer/8384442335428',
                8463093825732,
                8384442335428
            ];
            
            // Check if customer is whitelisted (convert to string for comparison)
            const customerIdStr = String(customerId);
            if (whitelistedCustomers.includes(customerId) || 
                whitelistedCustomers.includes(customerIdStr) ||
                whitelistedCustomers.includes(`gid://shopify/Customer/${customerIdStr}`)) {
                console.log('✅ Customer is whitelisted - bypassing subscription check', customerId);
                setHasActiveSubscription(true);
                setSubscriptionLoading(false);
                return;
            }
            
            // Use Recharge Storefront Client (works in browser without backend API token)
            initRecharge({
                storeIdentifier: "lexijwellness.myshopify.com",
                storefrontAccessToken: "strfnt_30bd64b6283b3ae215c5d1a77550fec7bde3adbca3da4911b7cdae8c56de2b1d",
            });
            
            const session = await loginShopifyAppProxy();
            
            if (!session || !session?.apiToken) {
                setHasActiveSubscription(false);
                setSubscriptionLoading(false);
                return;
            }
            
            const { charges } = await listCharges(session, {
                limit: 20,
                sort_by: "updated_at-desc"
            });
            
            if (!charges.length) {
                setHasActiveSubscription(false);
                setSubscriptionLoading(false);
                return;
            }
            
            const workoutCharges = charges.filter(({ line_items }) => 
                line_items.filter(({ title }) => title.includes("Workout"))
            );
            
            const today = new Date(Date.now());
            today.setHours(23, 59, 59, 59);
            
            let hasActive = false;
            
            for (let i = 0; i < workoutCharges.length; i++) {
                const c = workoutCharges[i];
                const termName = c.line_items[0].variant_title || c.line_items[0].title;
                
                let term;
                if (termName.includes("4 Weeks") || termName.includes("Monthly")) {
                    term = 28;
                } else if (termName.includes("12") || termName.includes("Quarterly")) {
                    term = 84;
                } else if (termName.includes("7-Day")) {
                    term = 7;
                } else if (termName.includes("1 Year")) {
                    term = 365;
                }
                
                if (!term) continue;
                
                const processed_at = c.processed_at;
                if (processed_at) {
                    const processedDate = new Date(processed_at);
                    const daysRemaining = Math.round(
                        (today.getTime() - processedDate.getTime()) / (1000 * 3600 * 24)
                    );
                    
                    if (daysRemaining > term) continue;
                }
                
                const chargeType = c.line_items[0].purchase_item_type;
                if (chargeType !== "subscription") continue;
                
                hasActive = true;
                break;
            }
            
            setHasActiveSubscription(hasActive);
        } catch (err) {
            console.error('Subscription check failed:', err);
            setHasActiveSubscription(false);
        } finally {
            setSubscriptionLoading(false);
        }
    }, []);

    // Check if user has store session (from Liquid)
    const checkStoreSession = useCallback(() => {
        const dataElement = document.getElementById('habit-tracker-data');
        if (dataElement) {
            try {
                const data = JSON.parse(dataElement.textContent);
                if (data.customer && data.customer.id) {
                    return data.customer;
                }
            } catch (e) {
                console.warn('Could not parse habit-tracker-data:', e);
            }
        }
        return null;
    }, []);

    // Auth check - use Liquid session only
    const checkAuth = useCallback(async () => {
        try {
            setIsLoading(true);
            
            // Check if user has store session (from Liquid template)
            const storeCustomer = checkStoreSession();
            
            if (!storeCustomer) {
                // No store session - shouldn't happen on /account/ pages
                console.log('🔐 No store session found');
                setIsAuthenticated(false);
                setCustomer(null);
                setSubscriptionLoading(false);
                setIsLoading(false);
                return;
            }
            
            // User has store session - set as authenticated
            console.log('✅ Authenticated via Shopify session:', storeCustomer.id);
            
            setCustomer({
                id: storeCustomer.id,
                firstName: storeCustomer.firstName,
                lastName: storeCustomer.lastName,
                email: storeCustomer.email
            });
            setIsAuthenticated(true);
            
            // Check subscription
            await checkSubscription(storeCustomer.id);
            
        } catch (err) {
            console.error('Auth check failed:', err);
            setIsAuthenticated(false);
            setError(err.message);
            setSubscriptionLoading(false);
        } finally {
            setIsLoading(false);
        }
    }, [checkStoreSession, checkSubscription]);

    useEffect(() => {
        // Normal auth check
        checkAuth();
    }, [checkAuth]);

    const fetchCustomerInfo = useCallback(async () => {
        return customer;
    }, [customer]);

    const logout = useCallback(() => {
        // Clear local state
        setIsAuthenticated(false);
        setCustomer(null);
        setError(null);
        setHasActiveSubscription(false);
        
        // Clear storage
        localStorage.removeItem('habit_tracker_data');
        sessionStorage.clear();
        
        // Redirect to Shopify logout
        window.location.href = '/account/logout';
    }, []);

    return (
        <AuthContext.Provider value={{
            // Auth state
            isAuthenticated,
            isLoading,
            customer,
            error,
            hasActiveSubscription,
            subscriptionLoading,
            
            // Auth methods
            logout,
            checkAuth,
            
            // Data fetching methods
            fetchCustomerInfo,
        }}>
            {children}
        </AuthContext.Provider>
    );
}
